import { useFetch } from "@hyper-fetch/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Check, Eye, EyeOff, Loader2, LogOut } from "lucide-react";
import { type ChangeEvent, type ComponentProps, type FormEvent, useCallback, useState } from "react";
import { toast } from "sonner";

import { sdk } from "@/api/sdk";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { consumeRedirectPath, resolveRedirect } from "@/features/auth/utils/post-auth-redirect";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

export function LoginForm({ className, ...props }: ComponentProps<"form">) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const { signIn, signOut, loginResponse, signInWithGoogle, googleSubmitting } = useAuth();
  const { data: userData, loading, setData } = useFetch(sdk.users.me.$get);

  const navigate = useNavigate();

  const redirectAfterAuth = useCallback(() => {
    const target = resolveRedirect(consumeRedirectPath());
    if ("external" in target) {
      window.location.assign(target.external);
      return;
    }
    navigate({ href: target.internal });
  }, [navigate]);

  // Already-signed-in users landing back on /login (common when the dev server
  // reloads) still honor a pending redirect, so "Open dashboard" sends them
  // where they were headed rather than always dumping them at the root.
  const handleOpenDashboard = useCallback(() => {
    redirectAfterAuth();
  }, [redirectAfterAuth]);

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    await signOut();
    // useFetch refetches on cache delete/invalidate, but clear the UI immediately
    // so the form swaps before the unauthenticated response lands.
    setData(null);
    setSigningOut(false);
  }, [setData, signOut]);

  const handleEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  }, []);

  const handlePasswordChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  }, []);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const { data: loginData, error } = await signIn({ email, password });

      if (loginData) {
        // No organization means the user hasn't finished onboarding yet.
        if (loginData.organizationId) {
          redirectAfterAuth();
        } else {
          const stored = consumeRedirectPath();
          navigate({ to: "/onboarding", search: stored ? { redirect: stored } : {} });
        }
      }

      if (error) {
        toast.error(error.message);
      }
    },
    [email, password, navigate, redirectAfterAuth, signIn],
  );

  const handleGoogleSignIn = useCallback(() => {
    signInWithGoogle("login");
  }, [signInWithGoogle]);

  if (loading) {
    return (
      <div className={cn("flex items-center justify-center gap-3 py-12", className)}>
        <Loader2 className="text-muted-foreground size-5 animate-spin" />
        <p className="text-muted-foreground text-sm">{m.auth_session_checking()}</p>
      </div>
    );
  }

  if (userData) {
    const { user } = userData;
    const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");
    const displayName = fullName || user.email;
    const initials = (fullName ? `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}` : user.email.slice(0, 2))
      .toUpperCase()
      .trim();
    const avatarSrc = userData.avatarUrl ?? user.avatarUrl ?? undefined;

    return (
      <div className={cn("flex flex-col items-center gap-7 text-center", className)}>
        <div className="relative">
          <div
            aria-hidden
            className="from-primary/40 via-secondary/25 absolute -inset-4 rounded-full bg-linear-to-tr to-transparent blur-2xl"
          />
          <Avatar className="border-background relative size-24 border-4 shadow-xl">
            <AvatarImage src={avatarSrc} alt={displayName} />
            <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <span
            aria-hidden
            className="border-background bg-primary text-primary-foreground absolute -right-0.5 -bottom-0.5 flex size-6 items-center justify-center rounded-full border-2 shadow"
          >
            <Check className="size-3.5" />
          </span>
        </div>

        <div className="space-y-1.5">
          <h1 className="text-2xl font-bold tracking-tight">{m.auth_login_welcome_back()}</h1>
          <p className="text-muted-foreground text-sm text-balance">
            {m.auth_login_signed_in_as()} <span className="text-foreground font-medium">{displayName}</span>
          </p>
        </div>

        <div className="flex w-full flex-col gap-2.5">
          <Button size="lg" className="group w-full" onClick={handleOpenDashboard}>
            {m.auth_login_open_dashboard()}
            <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </Button>
          <Button
            variant="ghost"
            className="text-muted-foreground hover:text-foreground w-full"
            onClick={handleSignOut}
            disabled={signingOut}
          >
            {signingOut ? <Loader2 className="size-4 animate-spin" /> : <LogOut className="size-4" />}
            {m.auth_sign_out()}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form {...props} className={cn("flex flex-col gap-6", className)} onSubmit={handleSubmit}>
      <FieldGroup>
        <div className="grid gap-1.5">
          <h1 className="text-2xl font-bold tracking-tight">{m.auth_login_title()}</h1>
          <p className="text-muted-foreground text-sm">{m.auth_login_description()}</p>
        </div>
        <Field>
          <FieldLabel htmlFor="email">{m.auth_field_email_label()}</FieldLabel>
          <Input
            id="email"
            type="email"
            placeholder={m.auth_field_email_placeholder()}
            required
            value={email}
            onChange={handleEmailChange}
          />
        </Field>
        <Field>
          <div className="flex items-center">
            <FieldLabel htmlFor="password">{m.auth_field_password_label()}</FieldLabel>
            <Link
              to="/new-password"
              className="text-primary ml-auto text-xs font-medium underline-offset-4 hover:underline"
            >
              {m.auth_login_forgot_password()}
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              inputClassName="pr-10"
              required
              value={password}
              onChange={handlePasswordChange}
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? m.auth_password_hide() : m.auth_password_show()}
              className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
            >
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
        </Field>
        <Field>
          <Button type="submit" disabled={loginResponse.submitting}>
            {loginResponse.submitting && <Loader2 className="size-4 animate-spin" />}
            {m.auth_login_submit()}
          </Button>
        </Field>
        <div className="flex items-center gap-4 text-sm" data-slot="field-separator">
          <Separator className="flex-1" />
          <span className="text-muted-foreground">{m.auth_or_continue_with()}</span>
          <Separator className="flex-1" />
        </div>
        <Field>
          <Button variant="outline" type="button" onClick={handleGoogleSignIn} disabled={googleSubmitting}>
            {googleSubmitting && <Loader2 className="size-4 animate-spin" />}
            {!googleSubmitting && (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path
                  d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                  fill="currentColor"
                />
              </svg>
            )}
            {m.auth_login_google()}
          </Button>
          <FieldDescription>
            {m.auth_login_no_account()}{" "}
            <Link to="/signup" className="text-primary font-medium underline-offset-4 hover:underline">
              {m.auth_login_signup_link()}
            </Link>
          </FieldDescription>
          <FieldDescription>
            {m.auth_legal_prefix()}{" "}
            <Link to="/terms" className="underline underline-offset-4">
              {m.auth_legal_terms()}
            </Link>{" "}
            {m.auth_legal_and()}{" "}
            <Link to="/privacy" className="underline underline-offset-4">
              {m.auth_legal_privacy_accepted()}
            </Link>
            .
          </FieldDescription>
        </Field>
      </FieldGroup>
    </form>
  );
}
